import { useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CountryCode } from 'libphonenumber-js';
import { t } from '@lingui/macro';

import KDSTextField from '@decisiv/ui-components/lib/components/TextField';
import { formatPhoneNumber } from 'utils/phoneFormatter';
import { TextFieldProps } from '../TextField';

interface PhoneTextFieldProps extends TextFieldProps {
  countryCode?: CountryCode;
}

const PhoneTextField = ({
  name,
  defaultValue,
  warningMessage,
  suppressWarningMessage,
  countryCode = 'US',
  ...props
}: PhoneTextFieldProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  // Set when the entered value contained something but no phone number could be
  // read out of it — the field then renders empty, and without this the input
  // would appear to have been ignored with nothing to correct. Not a form error:
  // an empty phone is legitimate when an email is present.
  const [inputWasUnreadable, setInputWasUnreadable] = useState(false);

  const fieldError = errors[name];

  const unreadableInputMessage = t`We couldn't read a phone number from that. Enter digits only, e.g. (555) 123-4567.`;

  const warningMessageToApply = useMemo(() => {
    if (suppressWarningMessage) return undefined;
    if (fieldError) return warningMessage || fieldError.message?.toString();

    return inputWasUnreadable ? unreadableInputMessage : undefined;
  }, [
    fieldError,
    inputWasUnreadable,
    suppressWarningMessage,
    unreadableInputMessage,
    warningMessage,
  ]);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => {
        const formattedValue = formatPhoneNumber(value, countryCode);

        // Store the formatted value, not the raw input, so state can't diverge
        // from what the field renders. Typing hid the old divergence by
        // round-tripping the formatted value on every keystroke; a paste or an
        // autofill sets the value in one event, leaving raw text in state that
        // then reached the SRM API verbatim (PD-4252). This syncs keystrokes and
        // pastes — not a later change of `countryCode`, which reformats the
        // display only.
        const handleChange = (raw: string) => {
          const formatted = formatPhoneNumber(raw, countryCode);

          setInputWasUnreadable(raw.trim().length > 0 && formatted === '');
          onChange(formatted);
        };

        return (
          <KDSTextField
            value={formattedValue}
            warningMessage={warningMessageToApply}
            onChange={({ target }) => handleChange(target.value)}
            {...props}
          />
        );
      }}
    />
  );
};

export default PhoneTextField;
